// Character traits for the walking sprite. Pure and seeded: save the seed + traits, never pixels.
// Body types: male, female, and anon (hood up, face hidden) because CT is pseudonymous.

import { Rng } from "../rng";

export type BodyType = "male" | "female" | "anon";

export type HairStyle =
  | "bald" | "buzz" | "spiky" | "sidepart" | "messy" | "mohawk" // male
  | "long" | "bob" | "ponytail" | "buns" | "wavy" // female
  | "hidden"; // anon (under the hood)
export type Headwear = "none" | "cap" | "backcap" | "beanie" | "crown" | "hood" | "headset";
export type EyeStyle = "normal" | "shades" | "laser" | "glasses3d";
export type MaskStyle = "none" | "balaclava" | "blankmask" | "visor" | "glitch" | "void";
export type TopStyle = "tee" | "hoodie" | "jacket" | "suit" | "tracksuit";
export type BottomStyle = "pants" | "shorts" | "skirt";
export type NeckStyle = "none" | "chain" | "diamond";

export interface AvatarTraits {
  skin: string;
  hair: HairStyle;
  hairColor: string;
  headwear: Headwear;
  headwearColor: string;
  eyes: EyeStyle;
  mask: MaskStyle;
  accent: string; // glow color for visors, void eyes, laser tint
  top: TopStyle;
  topColor: string;
  bottom: BottomStyle;
  bottomColor: string;
  shoes: string;
  neck: NeckStyle;
  beard: boolean;
  earring: boolean;
}

export interface AvatarIdentity {
  body: BodyType;
  seed: number;
  traits: AvatarTraits;
}

export const BODY_TYPES: BodyType[] = ["male", "female", "anon"];

export const SKIN_TONES = ["#ffdbb4", "#f1c27d", "#e0ac69", "#c68642", "#8d5524", "#5c3a21"];
const SKIN_RARE = "#9bc472"; // zombie green
const SKIN_GOLD = "#e8c547";
const HAIR_COLORS = ["#1c1a1f", "#3b2618", "#6b4226", "#d9b46a", "#b5532c", "#e8e4dc", "#ff6fae", "#4fa3ff", "#7cf29a"];
const CLOTH_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#6366f1", "#a855f7", "#ec4899", "#f4f4f5", "#27272a", "#475569"];
const PANTS_COLORS = ["#1e3a8a", "#27272a", "#3f3f46", "#57534e", "#365314", "#7c2d12", "#d6d3d1"];
const SHOE_COLORS = ["#f4f4f5", "#18181b", "#ef4444", "#3b82f6", "#a16207"];
const ACCENTS = ["#22d3ee", "#7cf29a", "#f472b6", "#facc15", "#a78bfa", "#ff3b3b"];

type Table<K extends string> = Record<K, number>;

const HAIR: Record<Exclude<BodyType, "anon">, Partial<Table<HairStyle>>> = {
  male: { buzz: 3, spiky: 3, sidepart: 3, messy: 3, mohawk: 1, bald: 1 },
  female: { long: 4, bob: 3, ponytail: 3, buns: 2, wavy: 3 },
};
const HEADWEAR: Table<Headwear> = { none: 10, cap: 3, backcap: 3, beanie: 3, hood: 2, headset: 2, crown: 0.3 };
const EYES: Table<EyeStyle> = { normal: 14, shades: 4, glasses3d: 1.5, laser: 0.6 };
const MASKS: Table<Exclude<MaskStyle, "none">> = { balaclava: 3, blankmask: 3, visor: 3, void: 2, glitch: 1.5 };
const TOPS: Table<TopStyle> = { tee: 4, hoodie: 4, jacket: 3, tracksuit: 2, suit: 1.5 };
const NECK: Table<NeckStyle> = { none: 10, chain: 2.5, diamond: 0.6 };

export function generateAvatar(seed: number, body: BodyType): AvatarIdentity {
  const rng = Rng.fromSeed(seed ^ 0xa5a5_1f1f);
  const anon = body === "anon";
  const skin = rng.chance(0.03) ? SKIN_RARE : rng.pick(SKIN_TONES);
  const hair = anon ? "hidden" : (rng.weighted(HAIR[body] as Table<HairStyle>) as HairStyle);
  let headwear: Headwear = anon ? "hood" : rng.weighted(HEADWEAR);
  // Long hair and buns don't fit under a cap/beanie in 24px; keep the silhouette readable.
  if ((hair === "buns" || hair === "mohawk") && headwear !== "none" && headwear !== "headset") headwear = "none";
  const top: TopStyle = anon || headwear === "hood" ? "hoodie" : rng.weighted(TOPS);
  const bottom: BottomStyle =
    body === "female" ? (rng.weighted({ pants: 3, skirt: 3, shorts: 2 }) as BottomStyle) : rng.chance(0.2) ? "shorts" : "pants";
  const topColor = top === "suit" ? rng.pick(["#1f2937", "#27272a", "#1e3a8a", "#f4f4f5"]) : rng.pick(CLOTH_COLORS);
  return {
    body,
    seed: seed >>> 0,
    traits: {
      skin,
      hair,
      hairColor: rng.pick(HAIR_COLORS),
      headwear,
      headwearColor: headwear === "hood" ? topColor : rng.pick(CLOTH_COLORS),
      eyes: anon ? "normal" : rng.weighted(EYES),
      mask: anon ? (rng.weighted(MASKS) as MaskStyle) : "none",
      accent: rng.pick(ACCENTS),
      top,
      topColor,
      bottom,
      bottomColor: rng.pick(PANTS_COLORS),
      shoes: rng.pick(SHOE_COLORS),
      neck: rng.weighted(NECK),
      beard: body === "male" && rng.chance(0.25),
      earring: body === "female" && rng.chance(0.35),
    },
  };
}

// ---------- PFP -> character ----------

/** PFP skin names -> sprite skin colors (kept in sync with pfp/generator PFP_SKIN). */
const PFP_SKIN_TO_SPRITE: Record<string, string> = {
  porcelain: SKIN_TONES[0], sand: SKIN_TONES[1], honey: SKIN_TONES[2], caramel: SKIN_TONES[3],
  cocoa: SKIN_TONES[4], espresso: SKIN_TONES[5], zombie: SKIN_RARE, gold: SKIN_GOLD,
};

/**
 * Build a walking character that matches a PFP: skin, headwear, eyewear, and jewelry carry over;
 * outfit and hair come from `outfitSeed` so the player can reroll the fit without changing the PFP.
 */
export function avatarFromPfp(pfp: Record<string, string>, body: BodyType, outfitSeed: number): AvatarIdentity {
  const av = generateAvatar(outfitSeed, body);
  const t = av.traits;
  t.skin = PFP_SKIN_TO_SPRITE[pfp.skin] ?? t.skin;
  if (pfp.eyes === "shades" || pfp.eyes === "laser" || pfp.eyes === "glasses3d") t.eyes = body === "anon" ? "normal" : pfp.eyes;
  else if (body !== "anon") t.eyes = "normal";
  if (pfp.accessory === "chain" || pfp.accessory === "diamond") t.neck = pfp.accessory;
  else t.neck = "none";
  if (body !== "anon") {
    const hw: Record<string, Headwear> = { none: "none", cap: "cap", backcap: "backcap", beanie: "beanie", bucket: "cap", hoodie: "hood", crown: "crown" };
    t.headwear = hw[pfp.headwear] ?? "none";
    if (t.headwear === "none" && pfp.accessory === "headset") t.headwear = "headset";
    if (t.headwear === "hood") t.top = "hoodie";
    if ((t.hair === "buns" || t.hair === "mohawk") && t.headwear !== "none" && t.headwear !== "headset") {
      t.hair = body === "female" ? "bob" : "buzz";
    }
    t.earring = body === "female" && pfp.accessory === "earring";
  }
  return av;
}
